// ─────────────────────────────────────────────────────────────────
// TERM ATTENDANCE REPORT — the printable version of the Term
// attendance tab.
//
// WHY IT EXISTS
//   Parents, heads and the district ask for attendance on paper, and
//   the screen version cannot be handed over: it is a grid of coloured
//   progress bars sized for a laptop. This is the same figures set out
//   the way an attendance record is actually read — one line per
//   learner, days present out of days marked, and a percentage.
//
//   Built as an HTML string opened in a new window, which is how every
//   other printable in this project works (receipts, the collection
//   sheet, report cards). It keeps the print stylesheet away from the
//   app's own CSS and lets the browser's print dialog do the paging.
//
// WHAT IT SHOWS
//   • The school letterhead, so the sheet stands on its own off-screen.
//   • Girls then boys, numbered — the order every paper register in the
//     school is already written in.
//   • Present / Absent / Days marked / % for each learner. THE
//     DENOMINATOR IS DAYS MARKED, not days on the calendar: a day the
//     register was never taken is nobody's absence, and the printed
//     sheet has to agree with the screen it came from.
//   • A day-by-day strip at the bottom — every date the register was
//     taken, with that day's present and absent counts. This is what
//     turns "17/20" into something a parent can query: they can see
//     which days were marked at all.
//   • Signature lines. A sheet that leaves the building gets signed.
// ─────────────────────────────────────────────────────────────────

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));

const isFemale = (g) => String(g || '').trim().toLowerCase().startsWith('f');

const prettyTerm = (t) => String(t || '').replace(/_/g, ' ');

// Dates are already 'YYYY-MM-DD'. Parse from the PARTS — `new
// Date('2026-08-29')` is read as UTC midnight and prints as the
// previous day anywhere behind UTC, which on an attendance record
// would silently shift every date by one.
const fmtLongDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString(undefined,
    { year: 'numeric', month: 'long', day: 'numeric' });
};

const fmtShortDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString(undefined,
    { weekday: 'short', day: 'numeric', month: 'short' });
};

const REPORT_CSS = `
  @page { size: A4 portrait; margin: 12mm; }
  body { margin: 0; background: #fff; }
  .ta-sheet {
    position: relative; overflow: hidden;
    background: #fff; color: #000;
    font-family: 'Times New Roman', Georgia, serif;
    padding: 20px 28px; max-width: 900px; margin: 0 auto;
    box-sizing: border-box;
  }
  .ta-watermark {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%) rotate(-22deg);
    width: 62%; max-width: 520px;
    opacity: 0.10; pointer-events: none; user-select: none; z-index: 0;
  }
  .ta-content { position: relative; z-index: 1; }
  .ta-header { display: table; width: 100%; }
  .ta-header > div { display: table-cell; vertical-align: middle; }
  .ta-logo { width: 66px; height: 66px; object-fit: contain; display: block; }
  .ta-school {
    font-weight: 800; font-size: 21px; text-decoration: underline;
    text-transform: uppercase; text-align: center; letter-spacing: 0.5px;
  }
  .ta-info { width: 100%; border-collapse: collapse; font-size: 11.5px; margin: 4px 0 6px 0; }
  .ta-info td { padding: 2px 6px; vertical-align: top; }
  .ta-info .lbl { font-weight: 700; white-space: nowrap; }
  .ta-divider { border: none; border-top: 1px solid #000; margin: 6px 0 10px 0; }
  .ta-title {
    text-align: center; font-weight: 800; font-size: 16px;
    text-decoration: underline; margin: 4px 0 10px 0;
  }
  .ta-meta { width: 100%; border-collapse: collapse; font-size: 11.5px; margin-bottom: 10px; }
  .ta-meta td { padding: 2px 6px; }
  .ta-meta b { font-weight: 700; }

  .ta-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  .ta-table th, .ta-table td { border: 1px solid #000; padding: 3px 5px; }
  .ta-table th { background: #ececec; text-align: center; font-weight: 700; }
  .ta-table td.idx { text-align: center; width: 26px; }
  .ta-table td.num { text-align: center; width: 52px; }
  .ta-table td.pct { text-align: right;  width: 46px; font-weight: 700; }
  /* A section heading INSIDE the table, so Girls and Boys stay one
     continuous numbered list across a page break instead of becoming
     two tables that can drift apart. */
  .ta-table tr.section td {
    background: #f6f6f6; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.6px; font-size: 10.5px;
  }
  .ta-table tr.totalrow td { background: #f0f0f0; font-weight: 800; }
  /* Repeat the header on every page and never split a learner's row. */
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }

  .ta-sub { margin-top: 16px; }
  .ta-sub h3 {
    font-size: 12px; margin: 0 0 5px 0; text-transform: uppercase;
    letter-spacing: 0.6px; border-bottom: 1px solid #000; padding-bottom: 3px;
  }
  .ta-days { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  .ta-days th, .ta-days td { border: 1px solid #000; padding: 3px 5px; text-align: center; }
  .ta-days th { background: #ececec; font-weight: 700; }

  .ta-note { font-size: 10px; margin-top: 10px; font-style: italic; }
  .ta-signatures { display: table; width: 100%; margin-top: 30px; font-size: 11.5px; }
  .ta-signatures > div { display: table-cell; padding: 0 18px; }
  .ta-signatures .line { border-top: 1px dotted #000; padding-top: 4px; font-weight: 700; }
  .ta-foot { margin-top: 14px; font-size: 9.5px; color: #444; text-align: right; }

  @media print {
    .ta-sheet { padding: 4mm; max-width: 100%; }
    .ta-watermark { opacity: 0.12; }
  }
`;

// One learner's line.
function learnerRow(n, r, daysMarked) {
  // `marked` is per-learner, and is NOT always daysMarked: someone who
  // joined in week three was not on the roll to be marked in week one,
  // and dividing them by the whole term would invent absences.
  const marked = r.marked || daysMarked || 0;
  const pct = r.pct == null ? null : r.pct;
  return `
    <tr>
      <td class="idx">${n}</td>
      <td>${esc(r.name)}${r.off_roll ? ' <i>(left)</i>' : ''}</td>
      <td>${esc(r.index_no || '')}</td>
      <td class="num">${Number(r.present) || 0}</td>
      <td class="num">${Number(r.absent) || 0}</td>
      <td class="num">${marked}</td>
      <td class="pct">${pct == null ? '—' : `${pct}%`}</td>
    </tr>`;
}

function buildReportHTML({ summary, school, cla }) {
  const rows = summary?.students || [];
  const daysMarked = Number(summary?.days_marked) || 0;
  const t = summary?.totals || {};
  const girls = rows.filter(r => isFemale(r.gender));
  const boys  = rows.filter(r => !isFemale(r.gender));

  const section = (title, list, startAt) => !list.length ? '' : `
    <tr class="section"><td colspan="7">${esc(title)} — ${list.length}</td></tr>
    ${list.map((r, i) => learnerRow(startAt + i, r, daysMarked)).join('')}`;

  const presentTotal = (Number(t.present_f) || 0) + (Number(t.present_m) || 0);
  const absentTotal  = (Number(t.absent_f)  || 0) + (Number(t.absent_m)  || 0);
  const possible     = presentTotal + absentTotal;
  const overallPct   = possible > 0 ? Math.round((presentTotal / possible) * 100) : null;

  const dayCells = (summary?.days || []).map(d => `
    <tr>
      <td>${esc(fmtShortDate(d.reg_date))}</td>
      <td>${Number(d.present) || 0}</td>
      <td>${Number(d.absent) || 0}</td>
    </tr>`).join('');

  const watermark = school?.logo
    ? `<img class="ta-watermark" src="${esc(school.logo)}" alt="" />` : '';
  const logo = school?.logo
    ? `<img class="ta-logo" src="${esc(school.logo)}" alt="logo" />` : '';

  return `
    ${watermark}
    <div class="ta-content">
      <div class="ta-header">
        <div style="width:66px;">${logo}</div>
        <div><div class="ta-school">${esc(school?.school_name || 'School Name')}</div></div>
        <div style="width:66px;"></div>
      </div>

      <table class="ta-info">
        <tr>
          <td class="lbl">Address:</td><td>${esc(school?.address || '—')}</td>
          <td class="lbl" style="text-align:right;">Tel#:</td>
          <td style="text-align:right;">${esc(school?.phone || '—')}</td>
        </tr>
        <tr>
          <td class="lbl">Location:</td><td>${esc(school?.region || '—')}</td>
          <td class="lbl" style="text-align:right;">Email:</td>
          <td style="text-align:right;">${esc(school?.email || '—')}</td>
        </tr>
      </table>
      <hr class="ta-divider" />

      <div class="ta-title">Term Attendance Report</div>

      <table class="ta-meta">
        <tr>
          <td><b>Class:</b> ${esc(cla || summary?.cla || '—')}</td>
          <td><b>Term:</b> ${esc(prettyTerm(summary?.term) || '—')}</td>
          <td><b>Academic Year:</b> ${esc(summary?.acc_yer || '—')}</td>
        </tr>
        <tr>
          <td><b>Period:</b> ${esc(fmtLongDate(summary?.from))} &ndash; ${esc(fmtLongDate(summary?.to))}</td>
          <td><b>Days Marked:</b> ${daysMarked}</td>
          <td><b>Learners on Roll:</b> ${rows.length}</td>
        </tr>
      </table>

      <table class="ta-table">
        <thead>
          <tr>
            <th style="width:26px;">#</th>
            <th>Student Name</th>
            <th style="width:88px;">Index No.</th>
            <th style="width:52px;">Present</th>
            <th style="width:52px;">Absent</th>
            <th style="width:52px;">Days<br/>Marked</th>
            <th style="width:46px;">%</th>
          </tr>
        </thead>
        <tbody>
          ${rows.length === 0
            ? `<tr><td colspan="7" style="text-align:center;padding:14px;">
                 No learners on the roll for this class.</td></tr>`
            : section('Girls', girls, 1) + section('Boys', boys, girls.length + 1)}
          <tr class="totalrow">
            <td colspan="3" style="text-align:right;">TOTAL</td>
            <td class="num">${presentTotal}</td>
            <td class="num">${absentTotal}</td>
            <td class="num">${possible}</td>
            <td class="pct">${overallPct == null ? '—' : `${overallPct}%`}</td>
          </tr>
        </tbody>
      </table>

      ${daysMarked > 0 ? `
      <div class="ta-sub">
        <h3>Days the register was taken (${daysMarked})</h3>
        <table class="ta-days">
          <thead>
            <tr><th style="width:40%;">Date</th><th>Present</th><th>Absent</th></tr>
          </thead>
          <tbody>${dayCells}</tbody>
        </table>
      </div>` : ''}

      <div class="ta-note">
        Attendance is counted out of the days the register was actually taken, not out of
        the days on the calendar. A day no register was taken is not recorded against any
        learner.
      </div>

      <div class="ta-signatures">
        <div><div class="line">Class Teacher</div></div>
        <div><div class="line">Headmaster</div></div>
      </div>

      <div class="ta-foot">Printed ${esc(new Date().toLocaleString())}</div>
    </div>
  `;
}

function fullDocument(innerHTML, title) {
  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>${REPORT_CSS}</style>
  </head><body><div class="ta-sheet">${innerHTML}</div></body></html>`;
}

// Open the report in its own window and raise the print dialog.
//
// Returns false when the browser blocked the pop-up so the caller can
// say so — a print button that silently does nothing is the worst
// possible outcome, and pop-up blocking is the usual cause.
export function printTermAttendance({ summary, school, cla }) {
  const title = `Attendance-${cla || summary?.cla || ''}-${summary?.acc_yer || ''}`
    .replace(/[^A-Za-z0-9_-]/g, '_');
  const w = window.open('', '_blank', 'width=1000,height=800');
  if (!w) return false;
  w.document.write(fullDocument(buildReportHTML({ summary, school, cla }), title));
  w.document.close();
  w.focus();
  // The logo is an <img>; printing immediately can catch it unloaded and
  // leave a blank letterhead on the paper. A short beat costs nothing
  // and the browser's own dialog is what the user waits on anyway.
  setTimeout(() => w.print(), 400);
  return true;
}
